/**
 * The per-brand mailbox state behind BOTH Scenario C routes: the section grid and the mailbox page.
 * Each route mounts its own reducer; that is the design, not a shortcut. The plaintext password in
 * `secrets` dies when the route unmounts, which is exactly what the one-time warning promises,
 * while everything else (`passwordSetAt` included) round-trips through the per-brand store, so the
 * grid shows "Set {date}" when the operator comes back from a reset.
 *
 * Owns: the active brand (from the `casinoInstance` slice), its seed variant, hydration from the
 * saved set or the seed, persistence on change, and the simulated first-load latency. Pure React
 * plus the area's shared hooks; it shares nothing with Scenario A or B by design.
 */
import React from 'react';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';

import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import {
  buildMailboxes,
  buildMailboxEntitlement,
  mailboxSeedVariantFor,
  type MailboxDomain,
  type MailboxEntitlement,
  type MailboxError,
  type MailboxPriceRead,
  type MailboxRecord,
} from '../../mock-data/mailbox-records';
import { buildMailboxUsage, fetchMockMailboxUsage } from '../../mock-data/mailbox-usage';
import { buildMailboxDomain, fetchMockMailboxPrice, pollMockMailboxDomain } from '../../mock-data/mailbox-domain';
import {
  MAILBOX_DOMAIN_POLL_MS,
  mailDomainOf,
  mailboxDomainPolls,
  mailboxesReducer,
  mailboxListReloadsOnNewToken,
  mailboxPollHaltedByAuth,
  type MailboxesAction,
  type MailboxesState,
} from './state';
import {
  resolveInitialMailboxes,
  resolveInitialEntitlement,
  resolveInitialMailDomain,
  saveBrandMailboxState,
  hasBrandMailboxRecord,
  loadBrandMailboxes,
} from './store';
import { useMailboxUsage, type MailboxUsageCache, type MailboxUsageReader } from './useMailboxUsage';
import { MAILBOX_SOURCE } from './api/source';
import { fetchDomainWithDns, fetchMailboxPrice, fetchMailboxSection, fetchUsage, type DnsVerification } from './api/client';
import { mergeBilling } from './api/billing';
import { takeMailboxError } from './errors';

/** Reading from ows-mail rather than the local seed. Under `unavailable` this hook never mounts: the
 *  section and the mailbox page render their unavailable notice instead (see `config/mailboxSource.ts`). */
const LIVE = MAILBOX_SOURCE === 'http';

/** The live source's stand-ins for the seed: nothing is known about a brand until the service says. */
const NO_ROWS: MailboxRecord[] = [];
const NO_ENTITLEMENT: MailboxEntitlement = { included: 1, used: 0 };

export type UseMailboxesOptions = {
  /** Poll the domain while it is still being set up (see `mailboxDomainPolls`). The section asks (it
   *  shows the setup notice), and so does the mailbox page (its waiting state lifts when the domain
   *  turns ready). */
  pollDomain?: boolean;
  /** Read what a paid mailbox costs (`GET /domains/{domain}/mailbox-price`). The section asks (its
   *  add card, create form and checkout state the price); the mailbox page does not. */
  readPrice?: boolean;
};

export type Mailboxes = {
  state: MailboxesState;
  dispatch: React.Dispatch<MailboxesAction>;
  /** The brand's mail domain (or the neutral fallback). */
  domain: string;
  /** The active brand's id; undefined until the brand list has resolved one. */
  brandId: string | undefined;
  /**
   * True until BOTH the brand list has loaded and the simulated first fetch has landed. A page
   * that decides something from the mailbox list (an unknown-id redirect, say) must wait for this:
   * on a hard refresh the first render has no brand yet, and the list is the empty variant-0 seed.
   */
  loading: boolean;
  /** The pristine seed for the active brand, for callers that compare against it. */
  seed: MailboxRecord[];
  /** One mailbox's usage as this page holds it: read separately from the list, lazily, once per
   *  mailbox for the life of the page (see `useMailboxUsage`). */
  usageOf: MailboxUsageCache['usageOf'];
  /** Ask for one mailbox's usage; a no-op once asked. The grid calls it as a tile scrolls into view,
   *  the mailbox page on mount (with `lastReceived`). */
  requestUsage: MailboxUsageCache['requestUsage'];
  /** Live source only: why the brand's mailboxes could not be read (`null` while they load or once
   *  they have). The service's own error when it answered, else `null` with `loadFailed` set. */
  loadError: MailboxError | null;
  /** Live source only: the read failed. The section is then empty, and says so. */
  loadFailed: boolean;
  /** Live source only: the provider's DNS lookup as the latest domain read reported it, `undefined`
   *  until a read has answered with one. NOT READ by the provisioning screen any more (it paces every
   *  row and waits on nothing); the domain poll (the section's and the mailbox page's), the only
   *  consumer of `fetchDomainWithDns`, still fills it. */
  domainDns: DnsVerification | undefined;
  /** The last domain read failed (`error` is the service's answer, `null` when it did not answer);
   *  `null` once a read succeeds again. */
  domainReadFailure: { error: MailboxError | null } | null;
  /** Read the domain once, now (the provisioning screen's "Try again"). Only ever a READ. */
  retryDomainRead: () => void;
  /** Re-read the live list now (a create that succeeded while the section was away). A no-op on the
   *  mock, which has no service to ask. */
  reloadList: () => void;
  /** What a paid mailbox costs, as the service answered, for this brand's domain. `loading` until it
   *  has, and always `loading` when the hook was not asked to read it. */
  price: MailboxPriceRead;
};

const PRICE_LOADING: MailboxPriceRead = { status: 'loading' };

export const useMailboxes = ({ pollDomain = false, readPrice = false }: UseMailboxesOptions = {}): Mailboxes => {
  const dispatch = useAppDispatch();
  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand, isEqual);
  const brandsLoading = useAppSelector((state) => state.casinoInstance.loading);
  const brandsLoaded = useAppSelector((state) => state.casinoInstance.loaded);

  // MainLayout already loads the brand list app-wide; this guarded one-shot makes the routes
  // self-sufficient if ever shown without that chrome, and is a no-op once loaded/loading.
  const requestedBrands = React.useRef(false);
  React.useEffect(() => {
    if (requestedBrands.current) return;
    requestedBrands.current = true;
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
  }, [dispatch, brandsLoaded, brandsLoading]);

  // Lowercase: the seed builds its addresses on it, and every address is lowercase.
  const domain = mailDomainOf(displayBrand?.casino_data?.details?.domain);

  // The seed variant, by the ONE formula (see mock-data/mailbox-records).
  const variant = React.useMemo<number>(() => mailboxSeedVariantFor(brands, displayBrand?.id), [brands, displayBrand]);

  const seed = React.useMemo(() => (LIVE ? NO_ROWS : buildMailboxes(variant, domain)), [variant, domain]);
  const seedUsage = React.useMemo(() => buildMailboxUsage(variant, domain), [variant, domain]);
  const seedEntitlement = React.useMemo(() => (LIVE ? NO_ENTITLEMENT : buildMailboxEntitlement(variant)), [variant]);
  const seedDomain = React.useMemo(() => (LIVE ? null : buildMailboxDomain(variant, domain)), [variant, domain]);
  const brandId = displayBrand?.id;

  // Start from the operator's saved set for this brand if they have changed anything, else the seed.
  // This rehydrates on every mount, so navigating away and back restores their mailboxes instead of
  // re-seeding from mock. `used` is recomputed from the resolved count so it can never drift.
  // The live source starts EMPTY and fills in from the service; a saved set is never shown in its place.
  const [state, mailboxDispatch] = React.useReducer(mailboxesReducer, undefined, () => {
    if (LIVE) return { mailboxes: NO_ROWS, entitlement: NO_ENTITLEMENT, mailDomain: null, secrets: {} };
    const mailboxes = resolveInitialMailboxes(brandId, seed);
    return {
      mailboxes,
      entitlement: resolveInitialEntitlement(brandId, seedEntitlement, mailboxes),
      mailDomain: resolveInitialMailDomain(brandId, seedDomain),
      secrets: {},
    };
  });

  // Which brand the reducer state was hydrated FOR. React state, not a ref, on purpose: the
  // persist effect below runs in the same commit as the hydrate effect, and a ref set there would
  // already read as the new brand while `state` still holds the previous brand's rows. On a hard
  // refresh that is the difference between writing an EMPTY record for the brand that just loaded
  // (and resolving it to empty forever after) and writing nothing until its rows have landed.
  // Live, nothing is hydrated until the service has answered.
  const [hydratedFor, setHydratedFor] = React.useState<string | undefined>(LIVE ? undefined : brandId);
  React.useEffect(() => {
    if (LIVE) return;
    const mailboxes = resolveInitialMailboxes(brandId, seed);
    mailboxDispatch({
      type: 'hydrate',
      mailboxes,
      entitlement: resolveInitialEntitlement(brandId, seedEntitlement, mailboxes),
      mailDomain: resolveInitialMailDomain(brandId, seedDomain),
    });
    setHydratedFor(brandId);
  }, [seed, seedEntitlement, seedDomain, brandId]);

  // LIVE: the brand's domain and mailboxes, read from the service on arrival and on a brand switch.
  // A brand with no domain on record has nothing to ask about: it is the unknown-domain state at
  // once. `cancelled` drops an answer for a brand (or domain) the section has already left.
  const hasDomain = Boolean(displayBrand?.casino_data?.details?.domain?.trim());
  // `token` is the owner token a failed read went out with, so an auth refusal can be told apart
  // from one the store has since moved past (see `reloadOnNewToken` below).
  const [load, setLoad] = React.useState<{ failed: boolean; error: MailboxError | null; token?: string | null }>({
    failed: false,
    error: null,
  });
  const accessToken = useAppSelector((s) => s.authentication.access_token ?? null);
  // The token for the list read to stamp a failure with, without making every token refresh a
  // reason to re-read a list that loaded fine. Synced before the read effect below runs.
  const tokenForRead = React.useRef(accessToken);
  React.useEffect(() => {
    tokenForRead.current = accessToken;
  }, [accessToken]);
  // AUTH RECOVERY, as the domain poll has it: a list read refused for the OWNER (401 / 403) holds
  // only while the store still has the token it went out with. A refreshed or new token (any other
  // call can refresh it) re-reads the list once, instead of saying "sign in again" until the
  // operator leaves. Bumping `reloadTick` is the re-read; the read clears `load`, so this fires once.
  const [reloadTick, setReloadTick] = React.useState(0);
  const reloadOnNewToken = LIVE && mailboxListReloadsOnNewToken(load, brandId, accessToken);
  React.useEffect(() => {
    if (reloadOnNewToken) setReloadTick((n) => n + 1);
  }, [reloadOnNewToken]);
  // Re-read the live list on demand (a create that succeeded while the section was away: the row on
  // screen must be the service's). A no-op on the mock, which has no service to ask.
  const reloadList = React.useCallback(() => {
    if (LIVE) setReloadTick((n) => n + 1);
  }, []);
  React.useEffect(() => {
    if (!LIVE || !brandId) return undefined;
    let cancelled = false;
    const settle = (mailboxes: MailboxRecord[], mailDomain: MailboxDomain | null): void => {
      mailboxDispatch({
        type: 'hydrate',
        mailboxes,
        entitlement: resolveInitialEntitlement(brandId, NO_ENTITLEMENT, mailboxes),
        mailDomain,
      });
      setHydratedFor(brandId);
    };
    // A new read: nothing is hydrated until it answers (so the old domain's rows are not shown, or
    // saved, as the new one's in the meantime).
    setLoad({ failed: false, error: null });
    setHydratedFor(undefined);
    if (!hasDomain) {
      // No domain on record: there is nothing the service could be asked, and nothing a create
      // could succeed on (the client refuses the fallback). Not the unknown-domain state, which
      // offers the first create: it takes every action away, like a failed read, and says why.
      // (A client-side reason in the service error's shape, so both pages show it the same way;
      // the message is an i18n key, rendered through the notice's <Text>.)
      setLoad({
        failed: true,
        error: { code: 'not_found', message: 'This brand has no domain on record, so it has no mail yet.' },
      });
      settle(NO_ROWS, null);
      return undefined;
    }
    const tokenAtRead = tokenForRead.current;
    fetchMailboxSection(domain).then(
      ({ mailDomain, accounts }) => {
        if (cancelled) return;
        settle(mergeBilling(accounts, loadBrandMailboxes(brandId)), mailDomain);
      },
      (rejection: unknown) => {
        if (cancelled) return;
        setLoad({ failed: true, error: takeMailboxError(rejection), token: tokenAtRead });
        settle(NO_ROWS, null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [brandId, domain, hasDomain, reloadTick]);

  // THE PRICE: read with the domain and the list, on arrival and on a brand (or domain) switch, so a
  // create screen never waits on a request to say what it costs. Its own read, so a failed price never
  // fails the list (the section then shows no price, and no paid create can be paid for). Keyed by
  // brand and domain, so another brand's answer is never shown as this one's. Read again only when the
  // list is (`reloadTick`: Try again, a held create's success, or the list's AUTH RECOVERY above), so a
  // price that failed with the list recovers with it rather than staying failed for the visit. A
  // re-read KEEPS the last good answer for its key, while it is in flight and if it fails: only a key
  // with no answer yet goes to loading or failed (see the global lesson on re-reads).
  const priceKey = `${brandId ?? ''}\n${domain}`;
  const [priceRead, setPriceRead] = React.useState<{ key: string; read: MailboxPriceRead } | null>(null);
  React.useEffect(() => {
    if (!readPrice || !brandId) return undefined;
    let cancelled = false;
    const key = `${brandId}\n${domain}`;
    // Keep a good answer this key already has; otherwise this read is the one that decides.
    const unlessHeld =
      (read: MailboxPriceRead) =>
      (prev: { key: string; read: MailboxPriceRead } | null): { key: string; read: MailboxPriceRead } =>
        prev && prev.key === key && prev.read.status === 'ready' ? prev : { key, read };
    setPriceRead(unlessHeld(PRICE_LOADING));
    // No domain on record: there is nothing the service could be asked.
    if (LIVE && !hasDomain) {
      setPriceRead({ key, read: { status: 'failed' } });
      return undefined;
    }
    (LIVE ? fetchMailboxPrice(domain) : fetchMockMailboxPrice()).then(
      (price) => {
        if (!cancelled) setPriceRead({ key, read: { status: 'ready', price } });
      },
      (rejection: unknown) => {
        if (cancelled) return;
        takeMailboxError(rejection);
        setPriceRead(unlessHeld({ status: 'failed' }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [readPrice, brandId, domain, hasDomain, reloadTick]);

  // Persist the operator's changes for this brand, so they survive navigation + reload. Only once
  // they have diverged from the pristine seed (or already have a saved set): merely viewing an
  // untouched brand leaves its live seed alone. Mailboxes, entitlement and domain are written
  // together (one atomic record), so a finished DNS poll survives a reload like a created mailbox
  // does. `secrets` is never passed here, and never persisted (see store.ts).
  React.useEffect(() => {
    if (!brandId || hydratedFor !== brandId) return;
    // A failed live read left the section empty, which is not the brand's state: saving it would
    // erase the local billing layer the next successful read merges against (see `api/billing.ts`).
    if (LIVE && load.failed) return;
    // Nor an unknown domain (a `404`): it has no rows to save, and a domain that is merely
    // misconfigured also answers 404, so saving would erase the billing layer of the right one.
    if (LIVE && state.mailDomain === null) return;
    if (hasBrandMailboxRecord(brandId) || !isEqual(state.mailboxes, seed) || !isEqual(state.mailDomain, seedDomain)) {
      saveBrandMailboxState(brandId, state.mailboxes, state.entitlement, state.mailDomain);
    }
  }, [brandId, hydratedFor, state.mailboxes, state.entitlement, state.mailDomain, seed, seedDomain, load.failed]);

  // THE DOMAIN POLL: while the domain is still being set up (see `mailboxDomainPolls`, the same rule
  // the notice and the cards read), re-read it every MAILBOX_DOMAIN_POLL_MS until the provider moves
  // it on. The loop is torn down when the domain is ready or leaves the race, on a brand switch (the state is re-hydrated first) and on
  // unmount; `cancelled` drops an answer that lands after any of those. Live, the read is
  // `GET /domains/{domain}`. A failed read is remembered (the provisioning screen shows it and offers
  // a re-read) and cleared by the next one that succeeds; the loop keeps asking either way.
  const liveCount = state.mailboxes.filter((m) => m.status !== 'deleted').length;
  const polling =
    pollDomain && brandId !== undefined && hydratedFor === brandId && mailboxDomainPolls(state.mailDomain, state.mailboxes);
  const { mailDomain } = state;
  // The provider's DNS lookup and the last read's failure, as the domain reads left them. The failure
  // feeds the provisioning screen's failure branch; the lookup is no longer read by that screen (it
  // paces every row), only filled by the poll. A brand's; dropped with its brand.
  const [domainDns, setDomainDns] = React.useState<{ brandId: string | undefined; dns: DnsVerification } | null>(null);
  // `token` is the owner token the failed read went out with: an auth refusal only holds while it is
  // still the token in the store (see `authRefused`).
  const [readFailure, setReadFailure] = React.useState<{
    brandId: string | undefined;
    error: MailboxError | null;
    token: string | null;
  } | null>(null);
  // One read of the domain: what each poll tick does, and what "Try again" does once. `isCurrent`
  // says whether the answer still belongs on screen (the poll loop's `cancelled`, or the brand's).
  // Reads overlap (the poll's, Try again) and answer in any order: an answer
  // older than one already applied is dropped, so a slow read that fails late cannot put a failure back
  // over a newer success. Ordered by when each read STARTED; never "latest started only", which would
  // starve every answer if each read took longer than the gap between them.
  const readsStarted = React.useRef(0);
  const readApplied = React.useRef(0);
  const readDomain = React.useCallback(
    (isCurrent: () => boolean): void => {
      if (mailDomain === null) return;
      readsStarted.current += 1;
      const seq = readsStarted.current;
      const tokenAtRead = accessToken;
      const applies = (): boolean => {
        if (!isCurrent() || seq < readApplied.current) return false;
        readApplied.current = seq;
        return true;
      };
      const read: Promise<{ mailDomain: MailboxDomain; dns?: DnsVerification }> = LIVE
        ? fetchDomainWithDns(domain)
        : pollMockMailboxDomain(mailDomain, liveCount).then((next) => ({ mailDomain: next }));
      read.then(
        (answer) => {
          if (!applies()) return;
          setReadFailure(null);
          if (answer.dns) setDomainDns({ brandId, dns: answer.dns });
          if (!isEqual(answer.mailDomain, mailDomain)) {
            mailboxDispatch({ type: 'mailDomainResolved', mailDomain: answer.mailDomain });
          }
        },
        (rejection: unknown) => {
          const error = takeMailboxError(rejection);
          if (applies()) setReadFailure({ brandId, error, token: tokenAtRead });
        },
      );
    },
    [mailDomain, domain, liveCount, brandId, accessToken],
  );
  // A read refused for the OWNER (401: the session no longer resolves; 403: the domain is not theirs)
  // never resolves by waiting, so the poll stops on it rather than re-asking every interval. It holds
  // only while the store still has the token that read went out with: a refreshed or new token (the
  // client reads it per call) resumes the poll, whose next read answers for itself.
  const authRefused = mailboxPollHaltedByAuth(readFailure, brandId, accessToken);
  React.useEffect(() => {
    if (!polling || authRefused) return undefined;
    let cancelled = false;
    const id = window.setInterval(() => readDomain(() => !cancelled), MAILBOX_DOMAIN_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [polling, authRefused, readDomain]);
  // The brand on screen now, for a re-read that answers after a brand switch.
  const currentBrand = React.useRef(brandId);
  currentBrand.current = brandId;
  const retryDomainRead = React.useCallback(() => {
    const forBrand = brandId;
    readDomain(() => currentBrand.current === forBrand);
  }, [readDomain, brandId]);

  // Randomised first-load delay, so the routes fill in like a real fetch would.
  const endpoint = useMockEndpoint(seed);

  // Usage is a separate read per mailbox, and a failed domain has no mail service to answer it. Live,
  // it is the service's answer (read through, never cached by it), scoped to the brand and domain.
  const domainStatus = state.mailDomain?.status;
  const usageScope = React.useMemo(() => (LIVE ? `${brandId ?? ''}\n${domain}` : seedUsage), [brandId, domain, seedUsage]);
  const readUsage = React.useCallback<MailboxUsageReader>(
    (mailbox, query) =>
      LIVE ? fetchUsage(domain, mailbox.email, query) : fetchMockMailboxUsage(seedUsage, mailbox, { query, domainStatus }),
    [domain, seedUsage, domainStatus],
  );
  const { usageOf, requestUsage } = useMailboxUsage(usageScope, readUsage);

  return {
    state,
    dispatch: mailboxDispatch,
    domain,
    brandId,
    loading: (!LIVE && endpoint.loading) || !brandsLoaded || hydratedFor !== brandId,
    seed,
    usageOf,
    requestUsage,
    loadError: load.error,
    loadFailed: load.failed,
    domainDns: domainDns && domainDns.brandId === brandId ? domainDns.dns : undefined,
    domainReadFailure: readFailure && readFailure.brandId === brandId ? { error: readFailure.error } : null,
    retryDomainRead,
    reloadList,
    price: priceRead && priceRead.key === priceKey ? priceRead.read : PRICE_LOADING,
  };
};

export default useMailboxes;
